import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useApiHandler from "../../hooks/ApiHandler";
import { Vendor } from "../../../service";
import Cookies from "universal-cookie";

const Step5 = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const { callApi, loading } = useApiHandler();
  const [redirecting, setRedirecting] = useState(false);
  const [vendor, setVendor] = useState(null);
  const cookies = new Cookies();
  const token = cookies.get("token");

  const onboardingStatus = vendor?.onboarding_status ?? vendor?.onboardingStatus ?? values?.stripe_onboarding_status; // fallback to form
  const chargesEnabled = (vendor?.charges_enabled ?? vendor?.chargesEnabled ?? values?.stripe_charges_enabled) ? true : false;
  const payoutsEnabled = (vendor?.payouts_enabled ?? vendor?.payoutsEnabled ?? values?.stripe_payouts_enabled) ? true : false;

  const isCompleteVendor = onboardingStatus === "complete" || chargesEnabled || payoutsEnabled || (vendor?.ready ? true : false);
  const isCompleteForm = !!values?.stripe_onboarding_complete || !!values?.stripe_ready || !!values?.stripe_charges_enabled;
  const isComplete = isCompleteVendor || isCompleteForm;

  const buttonLabel = useMemo(() => {
    if (isComplete) return "Stripe Connected";
    if (onboardingStatus === "needs_more_info") return "Finish Stripe Setup";
    return "Connect with Stripe";
  }, [isComplete, onboardingStatus]);

  // Human-readable status label and color coding
  const statusInfo = useMemo(() => {
    // Formatter: snake_case -> Capitalized Words
    const formatLabel = (s) =>
      (s || "")
        .split("_")
        .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ""))
        .join(" ");

    if (!onboardingStatus) {
      return { label: "Not started", color: "#d32f2f" }; // red
    }
    if (onboardingStatus === "complete") {
      return { label: "Complete", color: "#3CAB5C" }; // green
    }
    if (onboardingStatus === "needs_more_info") {
      return { label: "Needs more info", color: "#B26A00" }; // yellow/orange
    }
    // Default: neutral grey with formatted label
    return { label: formatLabel(onboardingStatus), color: "#767676" };
  }, [onboardingStatus]);

  const refreshVendor = async () => {
    try {
      await callApi(() => Vendor.getVendorMe(), {
        onSuccess: (res) => {
          const v = res?.data;
          setVendor(v);
          // Handle both camelCase and snake_case field names from backend
          const onboardingStatus = v?.onboardingStatus ?? v?.onboarding_status;
          const chargesEnabled = v?.chargesEnabled ?? v?.charges_enabled;
          const payoutsEnabled = v?.payoutsEnabled ?? v?.payouts_enabled;
          const ready = v?.ready;

          const isReallyComplete =
            onboardingStatus === "complete" ||
            chargesEnabled === true ||
            payoutsEnabled === true ||
            ready === true;

          setFieldValue(
            "stripe_onboarding_complete",
            isReallyComplete
          );
        },
      });
    } catch (e) {
      // Leave field as false on error
      setFieldValue("stripe_onboarding_complete", false);
    }
  };

  useEffect(() => {
    // On mount and when returning from Stripe, fetch vendor status to update UI
    refreshVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Stripe becomes complete, persist it immediately so user can resume later
  useEffect(() => {
    const persistStripeCompletion = async () => {
      if (!isCompleteForm && !isCompleteVendor) return;
      // Set form field in case it wasn't set yet
      if (!values?.stripe_onboarding_complete) {
        setFieldValue("stripe_onboarding_complete", true);
      }
      try {
        await callApi(
          () =>
            Vendor.vendorOnboardingPartial(
              { stripe_onboarding_complete: true },
              token
            ),
          {
            onSuccess: () => {},
          }
        );
      } catch (e) {
        // Non-blocking
      }
    };
    persistStripeCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleteVendor, isCompleteForm]);

  const handleStripeConnect = async () => {
    setRedirecting(true);
    try {
      await callApi(() => Vendor.startStripeOnboarding(), {
        onSuccess: (res) => {
          const url = res?.data?.url;
          if (url) {
            window.location.href = url;
          }
        },
        onError: () => setRedirecting(false),
        finallyCallback: () => {},
      });
    } catch (e) {
      setRedirecting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {isComplete ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <CheckCircleIcon sx={{ color: "#3CAB5C", fontSize: 48 }} />
          <Typography sx={{ color: "#242424", textAlign: "center" }} variant="h3">
            Stripe setup complete
          </Typography>
          <Typography sx={{ color: "#595959", textAlign: "center" }}>
            You're good to go. Click Next to continue.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleStripeConnect}
            endIcon={<OpenInNewIcon />}
            disabled={redirecting || loading}
            sx={{
              backgroundColor: "#3CAB5C",

              "&:hover": { backgroundColor: "#3CAB5C" },
            }}
          >
            {redirecting ? "Redirecting..." : buttonLabel}
          </Button>

          {errors?.stripe_onboarding_complete && touched?.stripe_onboarding_complete && (
            <Typography sx={{ color: "#d32f2f" }}>
              {errors.stripe_onboarding_complete}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: statusInfo.color, textAlign: "center" }}>
              Status: {statusInfo.label}
            </Typography>
            {onboardingStatus && onboardingStatus !== "complete" && (
              <Typography sx={{ color: "#767676", textAlign: "center" }}>
                If you left Stripe midway or they need more info, click "{buttonLabel}" to resume.
              </Typography>
            )}
            {onboardingStatus === "complete" && !(chargesEnabled && payoutsEnabled) && (
              <Typography sx={{ color: "#B26A00", textAlign: "center" }}>
                Note: Your account is created but not fully active yet. Payouts may be pending until Stripe finishes verification.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Step5;
